import { Filesystem, Directory } from '@capacitor/filesystem';
import { Toast } from '@capacitor/toast';
import { dbService } from './database';

class DownloadManager {
  private downloading = false;
  private progress = 0;

  // Download course content for offline access
  async downloadCourse(courseId: number): Promise<void> {
    if (this.downloading) {
      await Toast.show({ text: 'Download already in progress' });
      return;
    }

    this.downloading = true;
    this.progress = 0;

    try {
      await Toast.show({ text: '📥 Downloading course for offline access...' });

      // Fetch course data from API
      const response = await fetch(`https://safaricom.loholearning.co.ke/api/courses/${courseId}/offline-data`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      const courseData = await response.json();

      // Save course metadata
      await this.saveCourseMetadata(courseData);

      // Download lessons
      for (let i = 0; i < courseData.lessons.length; i++) {
        const lesson = courseData.lessons[i];
        await this.downloadLesson(lesson);
        this.progress = ((i + 1) / courseData.lessons.length) * 100;
        console.log(`📥 Progress: ${Math.round(this.progress)}%`);
      }

      // Download quizzes
      for (const quiz of courseData.quizzes) {
        await this.downloadQuiz(quiz);
      }

      // Mark course as downloaded
      await dbService.db?.run(
        `INSERT OR REPLACE INTO downloaded_courses (course_id, downloaded_at, last_synced) 
         VALUES (?, datetime('now'), datetime('now'))`,
        [courseId]
      );

      await Toast.show({ text: '✅ Course downloaded! Available offline.' });
    } catch (error) {
      console.error('❌ Download failed:', error);
      await Toast.show({ text: '❌ Download failed. Check connection.' });
    } finally {
      this.downloading = false;
    }
  }

  private async saveCourseMetadata(course: any): Promise<void> {
    await dbService.db?.run(
      `INSERT OR REPLACE INTO courses (id, title, description, thumbnail, downloaded) 
       VALUES (?, ?, ?, ?, 1)`,
      [course.id, course.title, course.description, course.thumbnail]
    );
  }

  private async downloadLesson(lesson: any): Promise<void> {
    // Save lesson to SQLite
    await dbService.saveLesson(lesson);

    // Download video if exists
    if (lesson.video_url) {
      await this.downloadFile(lesson.video_url, `lesson_${lesson.id}_video`);
    }

    // Download attachments
    if (lesson.attachments) {
      for (const attachment of lesson.attachments) {
        await this.downloadFile(attachment.url, `lesson_${lesson.id}_${attachment.name}`);
      }
    }
  }

  private async downloadQuiz(quiz: any): Promise<void> {
    await dbService.db?.run(
      `INSERT OR REPLACE INTO quizzes (id, lesson_id, title, questions, time_limit) 
       VALUES (?, ?, ?, ?, ?)`,
      [quiz.id, quiz.lesson_id, quiz.title, JSON.stringify(quiz.questions), quiz.time_limit]
    );
  }

  private async downloadFile(url: string, filename: string): Promise<void> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const base64 = await this.blobToBase64(blob);

      await Filesystem.writeFile({
        path: `elimu-pepe/${filename}`,
        data: base64,
        directory: Directory.Data
      });

      console.log(`✅ Downloaded: ${filename}`);
    } catch (error) {
      console.error(`❌ Failed to download ${filename}:`, error);
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Get downloaded courses
  async getDownloadedCourses(): Promise<any[]> {
    const result = await dbService.db?.query(
      'SELECT * FROM courses WHERE downloaded = 1'
    );
    return result?.values || [];
  }

  // Check if course is downloaded
  async isCourseDownloaded(courseId: number): Promise<boolean> {
    const result = await dbService.db?.query(
      'SELECT id FROM downloaded_courses WHERE course_id = ?',
      [courseId]
    );
    return (result?.values?.length || 0) > 0;
  }

  getProgress(): number {
    return this.progress;
  }
}

export const downloadManager = new DownloadManager();
